import { useState, useEffect } from 'react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';

// Owns all invoice mutation state + handlers: approve/reject adjustments, manual
// payments, and bulk/single cancel·void·mark-paid·reminder actions. Logic is a
// straight relocation from the page (no behavioural change).
export const useInvoiceActions = ({
  selectedIds, selectedInvoice, setSelectedInvoice, setDetailOpen, clearSelection, loadInvoices,
}) => {
  const [processing, setProcessing] = useState(false);
  const [voidCategories, setVoidCategories] = useState([]);
  const [cancelReasons, setCancelReasons] = useState([]);

  // Approve / reject
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Cancel
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');

  // Void
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidCategory, setVoidCategory] = useState('');
  const [voidReason, setVoidReason] = useState('');
  const [voidNotes, setVoidNotes] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [partialVoid, setPartialVoid] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [voidFromBulkAction, setVoidFromBulkAction] = useState(false);

  // Bulk pay
  const [bulkPayDialogOpen, setBulkPayDialogOpen] = useState(false);
  const [bulkPaymentType, setBulkPaymentType] = useState('');
  const [bulkPaymentComments, setBulkPaymentComments] = useState('');

  // Reminder
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);

  // Manual payment (single invoice)
  const [manualPaymentOpen, setManualPaymentOpen] = useState(false);
  const [manualPaymentInvoice, setManualPaymentInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [paymentComments, setPaymentComments] = useState('');
  const [applyingPayment, setApplyingPayment] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await adminService.getVoidCategories();
        setVoidCategories(data.void_categories || []);
        setCancelReasons(data.cancel_reasons || []);
      } catch (err) {
        console.error('Error loading void categories:', err);
      }
    })();
  }, []);

  const resetVoidForm = () => {
    setVoidCategory('');
    setVoidReason('');
    setVoidNotes('');
    setEstimatedCost('');
    setPartialVoid(false);
    setSelectedItems(new Set());
    setVoidFromBulkAction(false);
  };

  const handleApprove = async (invoiceId) => {
    setProcessing(true);
    try {
      await adminService.approveAdjustment(invoiceId);
      toast.success('Adjustment approved');
      loadInvoices();
      if (selectedInvoice?.id === invoiceId) {
        const updated = await adminService.getInvoiceDetail(invoiceId);
        setSelectedInvoice(updated);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setProcessing(true);
    try {
      await adminService.rejectAdjustment(selectedInvoice.id, rejectionReason);
      toast.success('Adjustment rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      loadInvoices();
      const updated = await adminService.getInvoiceDetail(selectedInvoice.id);
      setSelectedInvoice(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const openManualPaymentDialog = (invoice) => {
    setDetailOpen(false);
    setManualPaymentInvoice(invoice);
    const remainingAmount = invoice.amount_remaining ?? invoice.amount;
    setPaymentAmount(remainingAmount.toFixed(2));
    setPaymentType('');
    setPaymentComments('');
    setManualPaymentOpen(true);
  };

  const handleApplyManualPayment = async () => {
    if (!paymentType) {
      toast.error('Please select a payment type');
      return;
    }
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    const remainingAmount = manualPaymentInvoice.amount_remaining ?? manualPaymentInvoice.amount;
    if (amount > remainingAmount) {
      toast.error(`Amount cannot exceed remaining balance ($${remainingAmount.toFixed(2)})`);
      return;
    }
    setApplyingPayment(true);
    try {
      const result = await adminService.applyManualPayment(manualPaymentInvoice.id, {
        amount,
        payment_type: paymentType,
        comments: paymentComments,
      });
      if (result.is_fully_paid) {
        toast.success(`Payment applied - Invoice ${manualPaymentInvoice.invoice_number} is now fully paid!`);
      } else {
        toast.success(`Payment of $${amount.toFixed(2)} applied. Remaining balance: $${result.amount_remaining.toFixed(2)}`);
      }
      setManualPaymentOpen(false);
      loadInvoices();
      if (selectedInvoice?.id === manualPaymentInvoice.id) {
        const updated = await adminService.getInvoiceDetail(manualPaymentInvoice.id);
        setSelectedInvoice(updated);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply payment');
    } finally {
      setApplyingPayment(false);
    }
  };

  const reportFailures = (failed) => {
    (failed || []).forEach(f => toast.error(`${f.invoice_number || f.invoice_id}: ${f.reason}`));
  };

  const handleBulkCancel = async () => {
    if (!cancelReason) {
      toast.error('Please select a cancellation reason');
      return;
    }
    setProcessing(true);
    try {
      const result = await adminService.bulkCancelInvoices(Array.from(selectedIds), cancelReason, cancelNotes);
      toast.success(result.message);
      reportFailures(result.results.failed);
      setCancelDialogOpen(false);
      setCancelReason('');
      setCancelNotes('');
      clearSelection();
      loadInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel invoices');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkVoid = async () => {
    if (!voidCategory) {
      toast.error('Please select a void category');
      return;
    }
    if (!voidReason || voidReason.length < 10) {
      toast.error('Please provide a detailed void reason (at least 10 characters)');
      return;
    }
    setProcessing(true);
    try {
      const result = await adminService.bulkVoidInvoices(
        Array.from(selectedIds), voidCategory, voidReason,
        estimatedCost ? parseFloat(estimatedCost) : null, voidNotes
      );
      toast.success(result.message);
      if (result.results.credit_memos_created?.length > 0) {
        toast.success(`${result.results.credit_memos_created.length} credit memo(s) created`);
      }
      reportFailures(result.results.failed);
      setVoidDialogOpen(false);
      resetVoidForm();
      clearSelection();
      loadInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to void invoices');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkMarkPaid = async () => {
    if (!bulkPaymentType) {
      toast.error('Please select a payment type');
      return;
    }
    setProcessing(true);
    try {
      const result = await adminService.bulkMarkPaid(Array.from(selectedIds), bulkPaymentType, bulkPaymentComments);
      toast.success(result.message);
      reportFailures(result.results.failed);
      setBulkPayDialogOpen(false);
      setBulkPaymentType('');
      setBulkPaymentComments('');
      clearSelection();
      loadInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark invoices as paid');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkSendReminder = async () => {
    setProcessing(true);
    try {
      const result = await adminService.bulkSendReminder(Array.from(selectedIds));
      toast.success(result.message);
      reportFailures(result.results.failed);
      setReminderDialogOpen(false);
      clearSelection();
      loadInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setProcessing(false);
    }
  };

  const handleSingleVoid = async () => {
    if (!voidCategory) {
      toast.error('Please select a void category');
      return;
    }
    if (!voidReason || voidReason.length < 10) {
      toast.error('Please provide a detailed void reason (at least 10 characters)');
      return;
    }
    setProcessing(true);
    try {
      const voidData = {
        void_category: voidCategory,
        void_reason: voidReason,
        notes: voidNotes,
        estimated_production_cost: estimatedCost ? parseFloat(estimatedCost) : null,
        selected_item_ids: partialVoid && selectedItems.size > 0 ? Array.from(selectedItems) : null,
      };
      const result = await adminService.voidInvoice(selectedInvoice.id, voidData);
      toast.success(result.message);
      setVoidDialogOpen(false);
      setDetailOpen(false);
      resetVoidForm();
      loadInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to void invoice');
    } finally {
      setProcessing(false);
    }
  };

  return {
    processing, voidCategories, cancelReasons,
    rejectDialogOpen, setRejectDialogOpen, rejectionReason, setRejectionReason,
    cancelDialogOpen, setCancelDialogOpen, cancelReason, setCancelReason, cancelNotes, setCancelNotes,
    voidDialogOpen, setVoidDialogOpen, voidCategory, setVoidCategory, voidReason, setVoidReason,
    voidNotes, setVoidNotes, estimatedCost, setEstimatedCost, partialVoid, setPartialVoid,
    selectedItems, setSelectedItems, voidFromBulkAction, setVoidFromBulkAction,
    bulkPayDialogOpen, setBulkPayDialogOpen, bulkPaymentType, setBulkPaymentType, bulkPaymentComments, setBulkPaymentComments,
    reminderDialogOpen, setReminderDialogOpen,
    manualPaymentOpen, setManualPaymentOpen, manualPaymentInvoice, paymentAmount, setPaymentAmount,
    paymentType, setPaymentType, paymentComments, setPaymentComments, applyingPayment,
    handleApprove, handleReject, openManualPaymentDialog, handleApplyManualPayment,
    handleBulkCancel, handleBulkVoid, handleBulkMarkPaid, handleBulkSendReminder, handleSingleVoid,
    resetVoidForm,
  };
};
